import { useUser, useClerk } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { Check, LogOut, Building2, UserCog, Sun, Moon, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMe } from "@/lib/auth";
import {
  getSelectedTenantId,
  setSelectedTenantId,
} from "@/lib/tenant-selection";
import { isDevTestAuthActive } from "@/lib/dev-test-auth";
import { useTheme } from "@/components/theme-provider";

export function UserMenu() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { me } = useMe();
  const qc = useQueryClient();
  const { theme, setTheme } = useTheme();

  const devTestAuth = isDevTestAuthActive();
  if (!user && !devTestAuth) return null;
  const initials = (
    user?.firstName?.[0] ??
    user?.primaryEmailAddress?.emailAddress?.[0] ??
    me?.fullName?.[0] ??
    me?.email?.[0] ??
    "?"
  ).toUpperCase();
  const display =
    user?.fullName ||
    user?.primaryEmailAddress?.emailAddress ||
    me?.fullName ||
    me?.email ||
    "Account";

  const tenants = me?.tenants ?? [];
  const showSwitcher = tenants.length > 1;
  const currentId = getSelectedTenantId();
  const current = tenants.find((t) => t.id === currentId) ?? tenants[0];

  function switchTenant(id: string) {
    if (id === currentId) return;
    setSelectedTenantId(id);
    // Clear the entire cache so every page refetches under the new tenant
    // (queries don't share keys across tenants in the URL, so plain
    // invalidate would leave stale per-key data visible during the
    // refetch flash).
    qc.clear();
    // Hard reload so any module-level state (sidebar tree, branding, tab
    // state, etc.) re-initialises under the new tenant cleanly. Avoids
    // half-loaded mixed-tenant UI flashes that would otherwise need a
    // dozen ad-hoc invalidations.
    if (typeof window !== "undefined") window.location.reload();
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-11 w-full min-w-0 justify-start gap-2 px-1.5 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
          data-testid="button-user-menu"
        >
          {user?.imageUrl ? (
            <img src={user.imageUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
          ) : (
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">
              {initials}
            </div>
          )}
          <div className="min-w-0 flex flex-col items-start text-left leading-tight group-data-[collapsible=icon]:hidden">
            <span className="max-w-[155px] truncate text-xs font-medium">{display}</span>
            {showSwitcher && current ? (
              <span className="text-[10px] uppercase tracking-wider text-sidebar-foreground/60">
                {current.name}
              </span>
            ) : (
              me?.role && (
                <span className="text-[10px] uppercase tracking-wider text-sidebar-foreground/60">
                  {me.role}
                </span>
              )
            )}
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="flex flex-col gap-1">
          <span className="text-sm font-medium">{display}</span>
          {me && (
            <Badge variant="outline" className="w-fit capitalize">
              {me.role}
            </Badge>
          )}
        </DropdownMenuLabel>
        {showSwitcher && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Switch tenant
            </DropdownMenuLabel>
            {tenants.map((t) => {
              const active = t.id === current?.id;
              return (
                <DropdownMenuItem
                  key={t.id}
                  onSelect={() => switchTenant(t.id)}
                  className="gap-2"
                  data-testid={`menu-tenant-${t.slug}`}
                >
                  <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex min-w-0 flex-1 flex-col leading-tight">
                    <span className="truncate text-sm">{t.name}</span>
                    <span className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">
                      {t.role}
                    </span>
                  </div>
                  {active && <Check className="h-4 w-4 shrink-0 text-primary" />}
                </DropdownMenuItem>
              );
            })}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Theme
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => setTheme("light")}
          className="gap-2"
          data-testid="menu-theme-light"
        >
          <Sun className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="flex-1">Light</span>
          {theme === "light" && <Check className="h-4 w-4 shrink-0 text-primary" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme("dark")}
          className="gap-2"
          data-testid="menu-theme-dark"
        >
          <Moon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="flex-1">Dark</span>
          {theme === "dark" && <Check className="h-4 w-4 shrink-0 text-primary" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme("system")}
          className="gap-2"
          data-testid="menu-theme-system"
        >
          <Monitor className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="flex-1">System</span>
          {theme === "system" && <Check className="h-4 w-4 shrink-0 text-primary" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="gap-2" data-testid="menu-account">
          <Link href="/account">
            <UserCog className="h-4 w-4" />
            Account & password
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => signOut()}
          className="gap-2 text-destructive focus:text-destructive"
          data-testid="menu-sign-out"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
